import React, { useState } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader } from "reactstrap";
import Breadcrumbs from "../../../../Components/Common/Breadcrumb";
import {
  FlaskConical,
  Lightbulb,
  TestTube,
  Puzzle,
  Beaker,
  Rocket,
  Plane,
  CheckCircle2,
  Award,
  ChevronDown,
  ChevronUp,
  Shield,
  Cpu,
} from "lucide-react";

interface TRLLevel {
  level: number;
  title: string;
  description: string;
  examples: string[];
  icon: React.ReactNode;
  color: string;
  bgGradient: string;
  borderColor: string;
}

const DoWTRLAssessment: React.FC = () => {
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);

  const trlLevels: TRLLevel[] = [
    {
      level: 1,
      title: "Basic principles observed and reported",
      description:
        "Lowest level of technology readiness. Scientific research begins to be translated into applied research and development.",
      examples: ["Paper studies of a technology's basic properties"],
      icon: <FlaskConical className="w-8 h-8" />,
      color: "#dc3545",
      bgGradient: "linear-gradient(135deg, #ffe0e0 0%, #ffcccc 100%)",
      borderColor: "#dc3545",
    },
    {
      level: 2,
      title: "Technology concept and/or application formulated",
      description:
        "Invention begins. Once basic principles are observed, practical applications can be invented. Applications are speculative and there may be no proof or detailed analysis to support the assumptions.",
      examples: ["Limited to analytic studies"],
      icon: <Lightbulb className="w-8 h-8" />,
      color: "#fd7e14",
      bgGradient: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
      borderColor: "#fd7e14",
    },
    {
      level: 3,
      title: "Analytical and experimental critical function and/or characteristic proof of concept",
      description:
        "Active research and development is initiated. This includes analytical studies and laboratory studies to physically validate analytical predictions of separate elements of the technology.",
      examples: ["Components that are not yet integrated or representative"],
      icon: <TestTube className="w-8 h-8" />,
      color: "#ffc107",
      bgGradient: "linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)",
      borderColor: "#ffc107",
    },
    {
      level: 4,
      title: "Component and/or breadboard validation in laboratory environment",
      description:
        "Basic technological components are integrated to establish that they will work together. This is relatively 'low fidelity' compared to the eventual system.",
      examples: ["Integration of 'ad hoc' hardware in the laboratory"],
      icon: <Puzzle className="w-8 h-8" />,
      color: "#ffeb3b",
      bgGradient: "linear-gradient(135deg, #fffde7 0%, #fff9c4 100%)",
      borderColor: "#ffeb3b",
    },
    {
      level: 5,
      title: "Component and/or breadboard validation in relevant environment",
      description:
        "Fidelity of breadboard technology increases significantly. The basic technological components are integrated with reasonably realistic supporting elements so it can be tested in a simulated environment.",
      examples: ["'High fidelity' laboratory integration of components"],
      icon: <Beaker className="w-8 h-8" />,
      color: "#28a745",
      bgGradient: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
      borderColor: "#28a745",
    },
    {
      level: 6,
      title: "System/subsystem model or prototype demonstration in a relevant environment",
      description:
        "Representative model or prototype system, which is well beyond that of TRL 5, is tested in a relevant environment. Represents a major step up in a technology's demonstrated readiness.",
      examples: [
        "Testing a prototype in a high-fidelity laboratory environment",
        "Testing in simulated operational environment",
      ],
      icon: <Rocket className="w-8 h-8" />,
      color: "#17a2b8",
      bgGradient: "linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)",
      borderColor: "#17a2b8",
    },
    {
      level: 7,
      title: "System prototype demonstration in an operational environment",
      description:
        "Prototype near, or at, planned operational system. Represents a major step up from TRL 6, requiring demonstration of an actual system prototype in an operational environment such as an aircraft, vehicle, or space.",
      examples: ["Testing the prototype in a test bed aircraft"],
      icon: <Plane className="w-8 h-8" />,
      color: "#007bff",
      bgGradient: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
      borderColor: "#007bff",
    },
    {
      level: 8,
      title: "Actual system completed and qualified through test and demonstration",
      description:
        "Technology has been proven to work in its final form and under expected conditions. In almost all cases, this TRL represents the end of true system development.",
      examples: [
        "Developmental test and evaluation of the system in its intended weapon system to determine if it meets design specifications",
      ],
      icon: <CheckCircle2 className="w-8 h-8" />,
      color: "#6f42c1",
      bgGradient: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
      borderColor: "#6f42c1",
    },
    {
      level: 9,
      title: "Actual system proven through successful mission operations",
      description:
        "Actual application of the technology in its final form and under mission conditions, such as those encountered in operational test and evaluation.",
      examples: ["Using the system under operational mission conditions"],
      icon: <Award className="w-8 h-8" />,
      color: "#20c997",
      bgGradient: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
      borderColor: "#20c997",
    },
  ];

  const getProgressPercentage = (level: number) => {
    return (level / 9) * 100;
  };

  document.title = "DoD Technology Readiness Level Assessment | BizServe";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="DoD/War"
            breadcrumbItem="Technology Readiness Level Assessment"
          />

          {/* Header Section */}
          <Row className="mb-4">
            <Col>
              <Card className="border-0 shadow-sm" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                <CardBody className="p-4 text-white">
                  <div className="d-flex align-items-center mb-3">
                    <Shield className="w-10 h-10 me-3" />
                    <div>
                      <h2 className="mb-1 text-white">
                        Department of Defense Technology Readiness Levels
                      </h2>
                      <p className="mb-0 text-white-50">
                        Source: DoD (2010), Defense Acquisition Guidebook
                      </p>
                    </div>
                  </div>
                  <p className="mb-0 text-white-50">
                    Technology Readiness Levels (TRL) are a systematic
                    metric/measurement system that supports assessments of the
                    maturity of a particular technology and the consistent
                    comparison of maturity between different types of technology.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* TRL Levels Grid */}
          <Row className="g-4">
            {trlLevels.map((trl) => (
              <Col key={trl.level} lg={6} xl={4}>
                <Card
                  className="h-100 shadow-sm border-0 hover-lift"
                  style={{
                    borderLeft: `4px solid ${trl.borderColor}`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow =
                      "0 10px 25px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                  onClick={() =>
                    setExpandedLevel(expandedLevel === trl.level ? null : trl.level)
                  }
                >
                  <CardHeader
                    className="text-white border-0"
                    style={{
                      background: trl.bgGradient,
                      borderTopLeftRadius: "0.375rem",
                      borderTopRightRadius: "0.375rem",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div
                          className="me-3 d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: "56px",
                            height: "56px",
                            background: trl.color,
                            color: "white",
                          }}
                        >
                          {trl.icon}
                        </div>
                        <div>
                          <h5 className="mb-0 text-dark fw-bold">
                            TRL {trl.level}
                          </h5>
                          <div
                            className="progress mt-2"
                            style={{ height: "6px", width: "200px" }}
                          >
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{
                                width: `${getProgressPercentage(trl.level)}%`,
                                backgroundColor: trl.color,
                              }}
                              aria-valuenow={getProgressPercentage(trl.level)}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{ color: trl.color }}>
                        {expandedLevel === trl.level ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <h6
                      className="mb-3 fw-semibold"
                      style={{ color: trl.color, fontSize: "0.95rem" }}
                    >
                      {trl.title}
                    </h6>
                    <p className="text-muted mb-3" style={{ fontSize: "0.875rem" }}>
                      {trl.description}
                    </p>

                    {expandedLevel === trl.level && (
                      <div
                        className="mt-3 p-3 rounded"
                        style={{
                          background: trl.bgGradient,
                          border: `1px solid ${trl.borderColor}20`,
                        }}
                      >
                        <h6
                          className="mb-2 fw-semibold"
                          style={{ color: trl.color, fontSize: "0.875rem" }}
                        >
                          Examples:
                        </h6>
                        <ul className="mb-0" style={{ fontSize: "0.85rem" }}>
                          {trl.examples.map((example, idx) => (
                            <li key={idx} className="mb-1 text-dark">
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Summary Section */}
          <Row className="mt-5">
            <Col>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <h4 className="mb-4">
                    <Shield className="w-6 h-6 me-2 text-primary" />
                    Understanding TRL Levels
                  </h4>
                  <Row>
                    <Col md={4}>
                      <div className="mb-3">
                        <h6 className="text-danger fw-bold">TRL 1-3: Research</h6>
                        <p className="text-muted small mb-0">
                          Basic research and concept development phase. Technology
                          is still in early stages of development.
                        </p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <h6 className="text-warning fw-bold">TRL 4-6: Development</h6>
                        <p className="text-muted small mb-0">
                          Technology validation and integration phase. Components
                          are tested in laboratory and simulated environments.
                        </p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <h6 className="text-success fw-bold">TRL 7-9: Deployment</h6>
                        <p className="text-muted small mb-0">
                          System demonstration and operational phase. Technology is
                          proven in operational environments and ready for mission use.
                        </p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Visual Progress Bar */}
          <Row className="mt-4">
            <Col>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <h5 className="mb-4">TRL Maturity Scale</h5>
                  <div className="position-relative" style={{ height: "60px" }}>
                    {trlLevels.map((trl, idx) => (
                      <div
                        key={trl.level}
                        className="position-absolute d-flex flex-column align-items-center"
                        style={{
                          left: `${getProgressPercentage(trl.level) - 5.5}%`,
                          width: "11%",
                        }}
                      >
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                          style={{
                            width: "40px",
                            height: "40px",
                            background: trl.color,
                            color: "white",
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                          }}
                        >
                          {trl.level}
                        </div>
                        <div
                          className="text-center"
                          style={{
                            fontSize: "0.7rem",
                            color: trl.color,
                            fontWeight: "600",
                          }}
                        >
                          TRL {trl.level}
                        </div>
                      </div>
                    ))}
                    <div
                      className="position-absolute"
                      style={{
                        bottom: "0",
                        left: "0",
                        right: "0",
                        height: "8px",
                        background:
                          "linear-gradient(to right, #dc3545 0%, #fd7e14 11%, #ffc107 22%, #ffeb3b 33%, #28a745 44%, #17a2b8 55%, #007bff 66%, #6f42c1 77%, #20c997 100%)",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Computer Vision / AI Software Mapping Section */}
          <Row className="mt-5">
            <Col>
              <Card className="border-0 shadow-sm">
                <CardHeader
                  className="bg-primary text-white border-0"
                  style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <Cpu className="w-6 h-6 me-2" />
                    <h4 className="mb-0 text-white">
                      Mapping DoD Technology Readiness Levels (TRLs) to Computer
                      Vision and Artificial Intelligence (AI) Software
                    </h4>
                  </div>
                </CardHeader>
                <CardBody className="p-4">
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th
                            style={{
                              width: "8%",
                              fontWeight: "600",
                              verticalAlign: "middle",
                            }}
                          >
                            TRL Level
                          </th>
                          <th
                            style={{
                              width: "20%",
                              fontWeight: "600",
                              verticalAlign: "middle",
                            }}
                          >
                            DoD TRL Title
                          </th>
                          <th
                            style={{
                              width: "32%",
                              fontWeight: "600",
                              verticalAlign: "middle",
                            }}
                          >
                            Official DoD Description (verbatim)
                          </th>
                          <th
                            style={{
                              width: "40%",
                              fontWeight: "600",
                              verticalAlign: "middle",
                            }}
                          >
                            Computer Vision / AI Software
                            Interpretation
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#dc3545" }}>
                            TRL 1
                          </td>
                          <td className="fw-semibold">
                            Basic principles observed and reported
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Lowest level of technology readiness. Scientific research
                            begins to be translated into applied research and
                            development. Examples might include paper studies of a
                            technology's basic properties.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Research papers documenting fundamental computer vision or
                            machine learning principles. Theoretical analysis of
                            algorithms, mathematical foundations, or initial
                            observations of pattern recognition capabilities. No
                            functional software implementation exists. Datasets are
                            conceptual or not yet collected.
                          </td>
                        </tr>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#fd7e14" }}>
                            TRL 2
                          </td>
                          <td className="fw-semibold">
                            Technology concept and/or application formulated
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Invention begins. Once basic principles are observed,
                            practical applications can be invented. Applications are
                            speculative and there may be no proof or detailed analysis
                            to support the assumptions. Examples are limited to analytic
                            studies.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Conceptual design of a computer vision or AI application
                            with proposed use cases. Analytic studies or simulations
                            using synthetic or limited datasets. Algorithm selection and
                            theoretical performance projections. No validated software
                            implementation; analysis is primarily mathematical or
                            computational modeling.
                          </td>
                        </tr>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#ffc107" }}>
                            TRL 3
                          </td>
                          <td className="fw-semibold">
                            Analytical and experimental critical function and/or
                            characteristic proof of concept
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Active research and development is initiated. This includes
                            analytical studies and laboratory studies to physically
                            validate analytical predictions of separate elements of the
                            technology. Examples include components that are not yet
                            integrated or representative.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Individual algorithm components or model architectures tested
                            in isolation using research datasets. Proof-of-concept
                            implementations demonstrating core functionality (e.g.,
                            object detection, classification) on curated datasets.
                            Models run in development environments with manual
                            configuration. No integration with operational systems or
                            real-time data streams.
                          </td>
                        </tr>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#ffeb3b" }}>
                            TRL 4
                          </td>
                          <td className="fw-semibold">
                            Component and/or breadboard validation in laboratory
                            environment
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Basic technological components are integrated to establish
                            that they will work together. This is relatively "low
                            fidelity" compared to the eventual system. Examples include
                            integration of "ad hoc" hardware in the laboratory.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Multiple AI/ML components integrated into a functional
                            software system (e.g., data preprocessing, model inference,
                            post-processing). System operates in controlled laboratory
                            environment with standardized datasets. Model training and
                            inference pipelines are functional but not optimized for
                            production. Limited error handling and no integration with
                            external systems.
                          </td>
                        </tr>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#28a745" }}>
                            TRL 5
                          </td>
                          <td className="fw-semibold">
                            Component and/or breadboard validation in relevant
                            environment
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Fidelity of breadboard technology increases significantly.
                            The basic technological components are integrated with
                            reasonably realistic supporting elements so it can be
                            tested in a simulated environment. Examples include "high
                            fidelity" laboratory integration of components.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Integrated software system tested with representative
                            datasets that approximate operational conditions (e.g.,
                            domain-specific imagery, realistic noise, varied lighting).
                            System includes realistic data interfaces, basic error
                            handling, and logging. Models validated on datasets
                            representative of target operational environment. Testing
                            occurs in simulated runtime environment that mirrors
                            production constraints.
                          </td>
                        </tr>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#17a2b8" }}>
                            TRL 6
                          </td>
                          <td className="fw-semibold">
                            System/subsystem model or prototype demonstration in a
                            relevant environment
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Representative model or prototype system, which is well
                            beyond that of TRL 5, is tested in a relevant environment.
                            Represents a major step up in a technology's demonstrated
                            readiness. Examples include testing a prototype in a
                            high-fidelity laboratory environment or in simulated
                            operational environment.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Prototype system demonstrated in simulated operational
                            environment with representative hardware, data sources, and
                            integration points. System processes real-time or
                            near-real-time data streams with acceptable latency.
                            Models perform within specified accuracy thresholds on
                            operational-representative test sets. System includes
                            monitoring, basic failure recovery, and user interface for
                            operator interaction. Not yet deployed in actual mission
                            environment.
                          </td>
                        </tr>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#007bff" }}>
                            TRL 7
                          </td>
                          <td className="fw-semibold">
                            System prototype demonstration in an operational environment
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Prototype near, or at, planned operational system.
                            Represents a major step up from TRL 6, requiring
                            demonstration of an actual system prototype in an
                            operational environment such as an aircraft, vehicle, or
                            space. Examples include testing the prototype in a test bed
                            aircraft.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            System prototype deployed and operating in actual
                            operational environment (e.g., field test, test bed
                            platform, operational facility). Software integrated with
                            operational hardware, sensors, and data sources. Models
                            validated on operational data with documented performance
                            metrics. System demonstrates reliability, latency, and
                            accuracy under operational conditions. Operators trained and
                            using system for intended mission tasks. System may require
                            technical support and is not yet production-qualified.
                          </td>
                        </tr>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#6f42c1" }}>
                            TRL 8
                          </td>
                          <td className="fw-semibold">
                            Actual system completed and qualified through test and
                            demonstration
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Technology has been proven to work in its final form and
                            under expected conditions. In almost all cases, this TRL
                            represents the end of true system development. Examples
                            include developmental test and evaluation of the system in
                            its intended weapon system to determine if it meets design
                            specifications.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Production-ready software system qualified through
                            comprehensive testing and evaluation. System meets all
                            specified performance, reliability, and security
                            requirements. Models validated across full range of
                            operational conditions and edge cases. Software includes
                            production-grade error handling, monitoring, logging, and
                            maintenance capabilities. System documentation, training
                            materials, and support procedures are complete. Ready for
                            operational deployment pending final approval.
                          </td>
                        </tr>
                        <tr>
                          <td className="text-center fw-bold" style={{ color: "#20c997" }}>
                            TRL 9
                          </td>
                          <td className="fw-semibold">
                            Actual system proven through successful mission operations
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            Actual application of the technology in its final form and
                            under mission conditions, such as those encountered in
                            operational test and evaluation. Examples include using the
                            system under operational mission conditions.
                          </td>
                          <td style={{ fontSize: "0.9rem" }}>
                            System deployed and operating successfully in production
                            mission environment. Software demonstrates sustained
                            performance, reliability, and accuracy under actual mission
                            conditions. Models perform within specifications across
                            diverse operational scenarios. System integrated into
                            operational workflows with trained operators. Performance
                            data collected and analyzed to confirm system meets mission
                            requirements. System is mission-critical and operational.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Summary Paragraph */}
                  <div className="mt-4 p-4 bg-light rounded">
                    <h5 className="mb-3 fw-semibold">Summary</h5>
                    <p className="mb-0" style={{ fontSize: "0.95rem", lineHeight: "1.7" }}>
                      The progression of computer vision and AI
                      software through Technology Readiness Levels follows a structured
                      path from research to operational deployment. In the research
                      phase (TRL 1–3), fundamental algorithms and concepts are
                      developed and validated through theoretical analysis and
                      isolated component testing using research datasets. The laboratory
                      validation phase (TRL 4–5) involves integrating components into
                      functional systems and testing with representative datasets in
                      controlled environments that approximate operational conditions.
                      The integrated prototype phase (TRL 6) demonstrates the system in
                      simulated operational environments with realistic data streams and
                      integration points, establishing that the technology can perform
                      under operational-representative conditions. Finally, the
                      operational and production phase (TRL 7–9) involves deployment in
                      actual operational environments, comprehensive qualification
                      testing, and successful mission operations, where the software
                      demonstrates sustained performance, reliability, and integration
                      with operational systems under real-world mission conditions.
                    </p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .hover-lift {
          transition: all 0.3s ease;
        }
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
        }
      `}</style>
    </React.Fragment>
  );
};

export default DoWTRLAssessment;

